import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useCallback, useMemo } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';

import { type PixelType, useMultiplePixelOpportunitiesInfluenced } from '@/api/pixel';
import Flare, {
  Axis,
  type FlareSeriesOptions,
  Legend,
  Tooltip,
  linkifyAxisLabels,
  tooltipPositionBelow,
} from '@/components/Flare';
import AxisLabel from '@/components/Flare/guides/AxisLabel';
import Bar from '@/components/Flare/marks/Bar';
import { usePageFilterContext } from '@/components/page';
import { Panel } from '@/components/panels';
import { numberFormat } from '@/helper/numberFormatter';
import useInstanceNavigate from '@/hooks/useInstanceNavigate';
import { ROUTES } from '@/router';
import { COLOR_AQUA_200, COLOR_AQUA_600 } from '@/styles/palette';

type Props = {
  pixelIds?: string[];
  pixels?: PixelType[];
};

const PixelMultiOpportunityInfluenceChart = ({ pixelIds, pixels }: Props) => {
  const navigate = useInstanceNavigate();
  const { pageFilters } = usePageFilterContext();
  const { startDate, endDate } = pageFilters;
  const {
    pixelOpportunitiesInfluenced: data,
    isLoading,
    error,
  } = useMultiplePixelOpportunitiesInfluenced(pixelIds, startDate, endDate);

  const getAudienceName = useCallback(
    (groupId: string) => {
      const pixel = pixels?.find((p) => p.id === groupId);
      return pixel?.audience?.name;
    },
    [pixels],
  );

  const seriesData: FlareSeriesOptions[] | undefined = useMemo(() => {
    if (!data) {
      return undefined;
    }

    return [
      {
        id: 'pipeline',
        name: t`Pipeline Influence`,
        zIndex: 1,
        data: data.map((d) => ({
          amount: d.pipeline.totalRevenue,
          custom: { count: d.pipeline.totalOpportunities },
        })),
      },
      {
        id: 'closedWon',
        name: t`Closed Won Influence`,
        zIndex: 1,
        data: data.map((d) => ({
          amount: d.closedWon.totalRevenue,
          custom: { count: d.closedWon.totalOpportunities },
        })),
      },
    ];
  }, [data]);

  return (
    <Panel title={<Trans>Opportunity Influence</Trans>} noPadding style={{ height: 477 }}>
      <Flare
        data={seriesData}
        description={t`A breakdown of opportunity Pipeline and Closed Won revenue amounts for each pixel being compared`}
        colors={[COLOR_AQUA_600, COLOR_AQUA_200]}
        isLoading={isLoading}
        error={error}
        postRender={(chart) => {
          linkifyAxisLabels(chart.xAxis[0], (url) => navigate(url));
        }}
      >
        <Legend />
        <Bar
          y="amount"
          position="dodge"
          showDataLabels
          dataLabelFormat={(label) =>
            label.y === 0 ? '' : numberFormat(label.y, { isCurrency: true, precision: 0 })
          }
          skeletonLoaderOptions={{ numSeries: 3, numCategories: pixelIds?.length || 3 }}
        />
        <Axis
          position="right"
          labelFormat={(item) => numberFormat(item.value, { isCurrency: true, precision: 0 })}
          gridlineWidth={0}
          baselineWidth={1}
          tickAmount={5}
        />
        <Axis
          position="bottom"
          categories={data?.map((d) => d.groupName) || []}
          labelFormat={(item) => {
            const { groupId, groupName } = data?.[item.pos] || {};
            if (!groupId || !groupName) {
              return '';
            }
            return renderToStaticMarkup(
              <AxisLabel
                align="right"
                label={groupName}
                linkUrl={ROUTES.pixelById(groupId)}
                secondaryLabel={getAudienceName(groupId)}
              />,
            );
          }}
          useHTML
          crosshair="rect"
          baselineWidth={0}
        />
        <Tooltip
          titleFormat={(point) => point?.key}
          rowValueFormat={(point) =>
            t`${numberFormat(point?.y, { isCurrency: true, precision: 0 })} (${numberFormat(
              point?.custom?.count,
            )} Companies)`
          }
          positioner={tooltipPositionBelow}
          shared
        />
      </Flare>
    </Panel>
  );
};

export default PixelMultiOpportunityInfluenceChart;
